import AsyncStorage from '@react-native-async-storage/async-storage';

class ComprehensiveIntegrationService {
  constructor() {
    this.isInitialized = false;
    this.syncQueue = [];
    this.businessWorkflows = new Map();
    this.databaseConnections = new Map();
    this.retryAttempts = 3;
    this.syncTimeout = 30000; // 30 seconds
    this.offlineMode = false;
    
    // Business integration endpoints
    this.endpoints = {
      assessment: '/api/assessments',
      candidates: '/api/candidates',
      companies: '/api/companies',
      workflows: '/api/workflows',
      analytics: '/api/analytics',
      notifications: '/api/data/notifications',
      contracts: '/api/contracts',
      payroll: '/api/payroll'
    };
    
    // Initialize service
    this.initialize();
  }

  async initialize() {
    try {
      console.log('🚀 Initializing Comprehensive Integration Service...');
      
      // Initialize database connections
      await this.initializeDatabaseConnections();
      
      // Initialize business workflows
      await this.initializeBusinessWorkflows();
      
      // Setup offline sync
      await this.setupOfflineSync();
      
      // Initialize analytics tracking
      await this.initializeAnalytics();
      
      // Setup network monitoring
      this.setupNetworkMonitoring();
      
      this.isInitialized = true;
      console.log('✅ Comprehensive Integration Service initialized successfully');
      
      // Start background sync
      this.startBackgroundSync();
      
    } catch (error) {
      console.error('❌ Error initializing Comprehensive Integration Service:', error);
      // Continue with offline mode
      this.offlineMode = true;
      this.isInitialized = true;
    }
  }

  async initializeDatabaseConnections() {
    console.log('📊 Initializing database connections...');
    
    // Local database setup
    const localDbConfig = {
      name: 'jobeng_local.db',
      version: '1.0',
      tables: {
        assessments: 'assessments',
        candidates: 'candidates',
        applications: 'applications',
        companies: 'companies',
        workflows: 'workflows',
        analytics: 'analytics',
        sync_queue: 'sync_queue'
      }
    };
    
    // Remote database setup
    const remoteDbConfig = {
      host: process.env.DB_HOST || 'api.jobeng.ai',
      port: process.env.DB_PORT || 443,
      database: process.env.DB_NAME || 'jobeng_production',
      ssl: true,
      timeout: this.syncTimeout
    };
    
    this.databaseConnections.set('local', localDbConfig);
    this.databaseConnections.set('remote', remoteDbConfig);
    
    // Initialize local storage
    await this.initializeLocalStorage();
    
    console.log('✅ Database connections initialized');
  }

  async initializeLocalStorage() {
    const tables = [
      'assessments',
      'candidates',
      'applications',
      'companies',
      'workflows',
      'analytics',
      'sync_queue'
    ];
    
    for (const table of tables) {
      try {
        const existingData = await AsyncStorage.getItem(`table_${table}`);
        if (!existingData) {
          await AsyncStorage.setItem(`table_${table}`, JSON.stringify([]));
        }
      } catch (error) {
        console.error(`Error initializing table ${table}:`, error);
      }
    }
  }

  async initializeBusinessWorkflows() {
    console.log('🏢 Initializing business workflows...');
    
    // Assessment completion workflow
    this.businessWorkflows.set('assessment_completed', {
      id: 'assessment_completed',
      name: 'Assessment Completion Workflow',
      steps: [
        { id: 'validate_results', name: 'Validate Assessment Results', automated: true },
        { id: 'update_candidate_profile', name: 'Update Candidate Profile', automated: true },
        { id: 'notify_hiring_manager', name: 'Notify Hiring Manager', automated: true },
        { id: 'update_crm', name: 'Update CRM System', automated: true },
        { id: 'schedule_next_step', name: 'Schedule Next Interview Step', automated: false },
        { id: 'generate_report', name: 'Generate Assessment Report', automated: true },
        { id: 'update_analytics', name: 'Update Analytics Dashboard', automated: true }
      ],
      triggers: ['technical_assessment', 'psychometric_test', 'ai_interview'],
      priority: 'high'
    });
    
    // Application submission workflow
    this.businessWorkflows.set('application_submitted', {
      id: 'application_submitted',
      name: 'Application Submission Workflow',
      steps: [
        { id: 'validate_application', name: 'Validate Application Data', automated: true },
        { id: 'screen_candidate', name: 'Initial Candidate Screening', automated: true },
        { id: 'update_ats', name: 'Update Applicant Tracking System', automated: true },
        { id: 'notify_recruiter', name: 'Notify Recruiter', automated: true },
        { id: 'schedule_assessment', name: 'Schedule Technical Assessment', automated: false },
        { id: 'send_confirmation', name: 'Send Confirmation Email', automated: true }
      ],
      triggers: ['job_application'],
      priority: 'medium'
    });
    
    // Interview scheduling workflow
    this.businessWorkflows.set('interview_scheduled', {
      id: 'interview_scheduled',
      name: 'Interview Scheduling Workflow',
      steps: [
        { id: 'validate_schedule', name: 'Validate Schedule Availability', automated: true },
        { id: 'book_calendar', name: 'Book Calendar Slots', automated: true },
        { id: 'send_invitations', name: 'Send Interview Invitations', automated: true },
        { id: 'prepare_materials', name: 'Prepare Interview Materials', automated: true },
        { id: 'setup_video_call', name: 'Setup Video Call Link', automated: true },
        { id: 'send_reminders', name: 'Send Interview Reminders', automated: true }
      ],
      triggers: ['interview_request'],
      priority: 'high'
    });
    
    // Offer generation workflow
    this.businessWorkflows.set('offer_generated', {
      id: 'offer_generated',
      name: 'Job Offer Generation Workflow',
      steps: [
        { id: 'calculate_compensation', name: 'Calculate Compensation Package', automated: true },
        { id: 'generate_contract', name: 'Generate Employment Contract', automated: true },
        { id: 'legal_review', name: 'Legal Review and Approval', automated: false },
        { id: 'send_offer', name: 'Send Job Offer', automated: true },
        { id: 'track_response', name: 'Track Offer Response', automated: true },
        { id: 'update_systems', name: 'Update HR Systems', automated: true }
      ],
      triggers: ['interview_passed'],
      priority: 'critical'
    });
    
    console.log('✅ Business workflows initialized');
  }

  async setupOfflineSync() {
    console.log('🔄 Setting up offline sync...');
    
    // Load pending sync operations
    try {
      const pendingSync = await AsyncStorage.getItem('pending_sync_operations');
      if (pendingSync) {
        this.syncQueue = JSON.parse(pendingSync);
        console.log(`📋 Loaded ${this.syncQueue.length} pending sync operations`);
      }
    } catch (error) {
      console.error('Error loading pending sync operations:', error);
      this.syncQueue = [];
    }
    
    console.log('✅ Offline sync setup complete');
  }

  async initializeAnalytics() {
    console.log('📈 Initializing analytics tracking...');
    
    // Initialize analytics storage
    const analyticsData = {
      assessments: {
        total: 0,
        completed: 0,
        average_score: 0,
        completion_rate: 0
      },
      applications: {
        total: 0,
        successful: 0,
        pending: 0,
        success_rate: 0
      },
      interviews: {
        scheduled: 0,
        completed: 0,
        passed: 0,
        pass_rate: 0
      },
      business_metrics: {
        time_to_hire: 0,
        cost_per_hire: 0,
        candidate_satisfaction: 0,
        employer_satisfaction: 0
      }
    };
    
    try {
      const existingAnalytics = await AsyncStorage.getItem('analytics_data');
      if (!existingAnalytics) {
        await AsyncStorage.setItem('analytics_data', JSON.stringify(analyticsData));
      }
    } catch (error) {
      console.error('Error initializing analytics:', error);
    }
    
    console.log('✅ Analytics tracking initialized');
  }

  setupNetworkMonitoring() {
    console.log('🌐 Setting up network monitoring...');
    
    // Monitor network connectivity
    this.networkCheckInterval = setInterval(async () => {
      try {
        const response = await fetch('https://api.jobeng.ai/health', {
          method: 'GET',
          timeout: 5000
        });
        
        if (response.ok) {
          if (this.offlineMode) {
            console.log('🌐 Network restored, switching to online mode');
            this.offlineMode = false;
            await this.processPendingSync();
          }
        }
      } catch (error) {
        if (!this.offlineMode) {
          console.log('📱 Network unavailable, switching to offline mode');
          this.offlineMode = true;
        }
      }
    }, 30000); // Check every 30 seconds
    
    console.log('✅ Network monitoring setup complete');
  }

  async startBackgroundSync() {
    console.log('🔄 Starting background sync...');
    
    // Process sync queue every 60 seconds
    this.syncInterval = setInterval(async () => {
      if (!this.offlineMode && this.syncQueue.length > 0) {
        await this.processPendingSync();
      }
    }, 60000);
    
    console.log('✅ Background sync started');
  }

  // Assessment Integration Methods
  async saveAssessmentResults(assessmentData) {
    try {
      console.log('💾 Saving assessment results:', assessmentData.assessmentId);
      
      // Save locally first
      await this.saveToLocalDatabase('assessments', assessmentData);
      
      // Add to sync queue
      const syncOperation = {
        id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'assessment_results',
        operation: 'create',
        data: assessmentData,
        timestamp: new Date().toISOString(),
        retries: 0,
        priority: 'high'
      };
      
      await this.addToSyncQueue(syncOperation);
      
      // Trigger business workflow
      await this.triggerBusinessWorkflow('assessment_completed', assessmentData);
      
      // Update analytics
      await this.updateAnalytics('assessment_completed', assessmentData);
      
      console.log('✅ Assessment results saved successfully');
      return { success: true, id: assessmentData.assessmentId };
      
    } catch (error) {
      console.error('❌ Error saving assessment results:', error);
      throw new Error('Failed to save assessment results');
    }
  }

  async saveToLocalDatabase(table, data) {
    try {
      const existingData = await AsyncStorage.getItem(`table_${table}`);
      const tableData = existingData ? JSON.parse(existingData) : [];
      
      // Add or update record
      const existingIndex = tableData.findIndex(item => item.id === data.id);
      if (existingIndex >= 0) {
        tableData[existingIndex] = { ...tableData[existingIndex], ...data, updatedAt: new Date().toISOString() };
      } else {
        tableData.push({ ...data, createdAt: new Date().toISOString() });
      }
      
      await AsyncStorage.setItem(`table_${table}`, JSON.stringify(tableData));
      console.log(`💾 Data saved to local table: ${table}`);
      
    } catch (error) {
      console.error(`Error saving to local table ${table}:`, error);
      throw error;
    }
  }

  async addToSyncQueue(operation) {
    try {
      this.syncQueue.push(operation);
      await AsyncStorage.setItem('pending_sync_operations', JSON.stringify(this.syncQueue));
      console.log(`📋 Added operation to sync queue: ${operation.id}`);
      
      // Try immediate sync if online
      if (!this.offlineMode) {
        await this.processSingleSync(operation);
      }
      
    } catch (error) {
      console.error('Error adding to sync queue:', error);
    }
  }

  async processPendingSync() {
    if (this.syncQueue.length === 0) return;
    
    console.log(`🔄 Processing ${this.syncQueue.length} pending sync operations...`);
    
    const processedOperations = [];
    
    for (const operation of this.syncQueue) {
      try {
        const result = await this.processSingleSync(operation);
        if (result.success) {
          processedOperations.push(operation.id);
        }
      } catch (error) {
        console.error(`❌ Sync failed for operation ${operation.id}:`, error);
        
        // Increment retry count
        operation.retries = (operation.retries || 0) + 1;
        
        // Remove if max retries exceeded
        if (operation.retries >= this.retryAttempts) {
          console.log(`🗑️ Removing operation ${operation.id} after ${this.retryAttempts} failed attempts`);
          processedOperations.push(operation.id);
        }
      }
    }
    
    // Remove processed operations
    this.syncQueue = this.syncQueue.filter(op => !processedOperations.includes(op.id));
    await AsyncStorage.setItem('pending_sync_operations', JSON.stringify(this.syncQueue));
    
    console.log(`✅ Processed ${processedOperations.length} sync operations`);
  }

  async processSingleSync(operation) {
    try {
      console.log(`🔄 Syncing operation: ${operation.id}`);
      
      const endpoint = this.getEndpointForOperation(operation);
      const method = this.getMethodForOperation(operation);
      
      const response = await this.makeApiRequest(endpoint, method, operation.data);
      
      if (response.ok) {
        console.log(`✅ Sync successful for operation: ${operation.id}`);
        
        // Update local data with server response if needed
        if (response.data) {
          await this.updateLocalDataFromServer(operation, response.data);
        }
        
        return { success: true, data: response.data };
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
    } catch (error) {
      console.error(`❌ Sync failed for operation ${operation.id}:`, error);
      throw error;
    }
  }

  async makeApiRequest(endpoint, method, data) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.syncTimeout);
      
      const response = await fetch(`https://api.jobeng.ai/${endpoint}`, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`,
          'X-Client-Version': '1.0.0',
          'X-Platform': 'mobile'
        },
        body: method !== 'GET' ? JSON.stringify(data) : undefined,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const responseData = await response.json();
      
      return {
        ok: response.ok,
        status: response.status,
        data: responseData
      };
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Network timeout');
      }
      throw error;
    }
  }

  async getAuthToken() {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      return token || 'demo_token_for_development';
    } catch (error) {
      return 'demo_token_for_development';
    }
  }

  getEndpointForOperation(operation) {
    switch (operation.type) {
      case 'assessment_results':
        return this.endpoints.assessment;
      case 'job_application':
        return this.endpoints.candidates;
      case 'interview_schedule':
        return this.endpoints.workflows;
      default:
        return this.endpoints.assessment;
    }
  }

  getMethodForOperation(operation) {
    switch (operation.operation) {
      case 'create':
        return 'POST';
      case 'update':
        return 'PUT';
      case 'delete':
        return 'DELETE';
      default:
        return 'POST';
    }
  }

  async updateLocalDataFromServer(operation, serverData) {
    try {
      // Update local data with server response
      const table = this.getTableForOperation(operation);
      await this.saveToLocalDatabase(table, serverData);
    } catch (error) {
      console.error('Error updating local data from server:', error);
    }
  }

  getTableForOperation(operation) {
    switch (operation.type) {
      case 'assessment_results':
        return 'assessments';
      case 'job_application':
        return 'applications';
      case 'interview_schedule':
        return 'workflows';
      default:
        return 'assessments';
    }
  }

  // Business Workflow Methods
  async triggerBusinessWorkflow(workflowId, data) {
    try {
      console.log(`🏢 Triggering business workflow: ${workflowId}`);
      
      const workflow = this.businessWorkflows.get(workflowId);
      if (!workflow) {
        console.warn(`⚠️ Workflow not found: ${workflowId}`);
        return;
      }
      
      // Execute automated steps
      for (const step of workflow.steps) {
        if (step.automated) {
          await this.executeWorkflowStep(workflowId, step, data);
        } else {
          // Queue manual step for later processing
          await this.queueManualStep(workflowId, step, data);
        }
      }
      
      console.log(`✅ Business workflow completed: ${workflowId}`);
      
    } catch (error) {
      console.error(`❌ Error in business workflow ${workflowId}:`, error);
    }
  }

  async executeWorkflowStep(workflowId, step, data) {
    try {
      console.log(`⚙️ Executing workflow step: ${step.name}`);
      
      switch (step.id) {
        case 'validate_results':
          await this.validateAssessmentResults(data);
          break;
        case 'update_candidate_profile':
          await this.updateCandidateProfile(data);
          break;
        case 'notify_hiring_manager':
          await this.notifyHiringManager(data);
          break;
        case 'update_crm':
          await this.updateCRMSystem(data);
          break;
        case 'generate_report':
          await this.generateAssessmentReport(data);
          break;
        case 'update_analytics':
          await this.updateAnalytics('workflow_step', { step: step.id, data });
          break;
        default:
          console.log(`ℹ️ Unknown workflow step: ${step.id}`);
      }
      
    } catch (error) {
      console.error(`❌ Error executing workflow step ${step.id}:`, error);
    }
  }

  async validateAssessmentResults(data) {
    // Validate assessment data integrity
    const requiredFields = ['assessmentId', 'candidateId', 'score', 'type'];
    for (const field of requiredFields) {
      if (!data[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }
    
    // Validate score range
    if (data.score < 0 || data.score > 100) {
      throw new Error('Invalid score range');
    }
    
    console.log('✅ Assessment results validated');
  }

  async updateCandidateProfile(data) {
    try {
      const candidateUpdate = {
        id: data.candidateId,
        lastAssessment: data.assessmentId,
        lastAssessmentScore: data.score,
        lastAssessmentDate: new Date().toISOString(),
        assessmentHistory: data
      };
      
      await this.saveToLocalDatabase('candidates', candidateUpdate);
      
      // Add to sync queue
      const syncOperation = {
        id: `sync_${Date.now()}_candidate_update`,
        type: 'candidate_update',
        operation: 'update',
        data: candidateUpdate,
        timestamp: new Date().toISOString(),
        retries: 0,
        priority: 'medium'
      };
      
      await this.addToSyncQueue(syncOperation);
      
      console.log('✅ Candidate profile updated');
      
    } catch (error) {
      console.error('❌ Error updating candidate profile:', error);
    }
  }

  async notifyHiringManager(data) {
    try {
      const notification = {
        id: `notification_${Date.now()}`,
        type: 'assessment_completed',
        recipient: 'hiring_manager',
        candidateId: data.candidateId,
        assessmentId: data.assessmentId,
        score: data.score,
        message: `Assessment completed for candidate ${data.candidateId} with score ${data.score}%`,
        timestamp: new Date().toISOString(),
        priority: data.score >= 80 ? 'high' : 'medium'
      };
      
      await this.saveToLocalDatabase('notifications', notification);
      
      // Add to sync queue for email/SMS delivery
      const syncOperation = {
        id: `sync_${Date.now()}_notification`,
        type: 'notification',
        operation: 'create',
        data: notification,
        timestamp: new Date().toISOString(),
        retries: 0,
        priority: 'high'
      };
      
      await this.addToSyncQueue(syncOperation);
      
      console.log('✅ Hiring manager notified');
      
    } catch (error) {
      console.error('❌ Error notifying hiring manager:', error);
    }
  }

  async updateCRMSystem(data) {
    try {
      const crmUpdate = {
        candidateId: data.candidateId,
        assessmentScore: data.score,
        assessmentType: data.type,
        status: data.score >= 70 ? 'qualified' : 'needs_review',
        lastActivity: new Date().toISOString(),
        nextAction: data.score >= 80 ? 'schedule_interview' : 'review_assessment'
      };
      
      // Add to sync queue for CRM update
      const syncOperation = {
        id: `sync_${Date.now()}_crm_update`,
        type: 'crm_update',
        operation: 'update',
        data: crmUpdate,
        timestamp: new Date().toISOString(),
        retries: 0,
        priority: 'medium'
      };
      
      await this.addToSyncQueue(syncOperation);
      
      console.log('✅ CRM system update queued');
      
    } catch (error) {
      console.error('❌ Error updating CRM system:', error);
    }
  }

  async generateAssessmentReport(data) {
    try {
      const report = {
        id: `report_${data.assessmentId}`,
        assessmentId: data.assessmentId,
        candidateId: data.candidateId,
        generatedAt: new Date().toISOString(),
        summary: {
          overallScore: data.score,
          strengths: data.strengths || [],
          improvements: data.improvements || [],
          recommendations: data.recommendations || []
        },
        breakdown: data.breakdown || {},
        conclusion: this.generateReportConclusion(data.score),
        nextSteps: this.generateNextSteps(data.score)
      };
      
      await this.saveToLocalDatabase('reports', report);
      
      console.log('✅ Assessment report generated');
      return report;
      
    } catch (error) {
      console.error('❌ Error generating assessment report:', error);
    }
  }

  generateReportConclusion(score) {
    if (score >= 80) {
      return 'Excellent performance. Candidate demonstrates strong technical skills and problem-solving abilities. Highly recommended for next interview stage.';
    } else if (score >= 60) {
      return 'Good performance with some areas for improvement. Candidate shows potential and may benefit from additional training or mentoring.';
    } else {
      return 'Performance below expectations. Candidate may need significant development or may not be suitable for this role at this time.';
    }
  }

  generateNextSteps(score) {
    if (score >= 80) {
      return ['Schedule technical interview', 'Prepare advanced technical questions', 'Check references'];
    } else if (score >= 60) {
      return ['Review assessment details', 'Consider additional screening', 'Discuss with hiring team'];
    } else {
      return ['Provide feedback to candidate', 'Consider alternative roles', 'Update candidate status'];
    }
  }

  async queueManualStep(workflowId, step, data) {
    try {
      const manualTask = {
        id: `task_${Date.now()}`,
        workflowId: workflowId,
        stepId: step.id,
        stepName: step.name,
        data: data,
        status: 'pending',
        assignedTo: 'hiring_manager',
        createdAt: new Date().toISOString(),
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours from now
      };
      
      await this.saveToLocalDatabase('manual_tasks', manualTask);
      
      console.log(`📋 Manual task queued: ${step.name}`);
      
    } catch (error) {
      console.error('❌ Error queuing manual step:', error);
    }
  }

  // Analytics Methods
  async updateAnalytics(eventType, data) {
    try {
      const analyticsData = await this.getAnalyticsData();
      
      switch (eventType) {
        case 'assessment_completed':
          analyticsData.assessments.total += 1;
          analyticsData.assessments.completed += 1;
          analyticsData.assessments.average_score = this.calculateAverageScore(analyticsData, data.score);
          analyticsData.assessments.completion_rate = (analyticsData.assessments.completed / analyticsData.assessments.total) * 100;
          break;
          
        case 'application_submitted':
          analyticsData.applications.total += 1;
          analyticsData.applications.pending += 1;
          break;
          
        case 'interview_scheduled':
          analyticsData.interviews.scheduled += 1;
          break;
          
        case 'interview_completed':
          analyticsData.interviews.completed += 1;
          if (data.passed) {
            analyticsData.interviews.passed += 1;
          }
          analyticsData.interviews.pass_rate = (analyticsData.interviews.passed / analyticsData.interviews.completed) * 100;
          break;
      }
      
      await AsyncStorage.setItem('analytics_data', JSON.stringify(analyticsData));
      
      // Add to sync queue
      const syncOperation = {
        id: `sync_${Date.now()}_analytics`,
        type: 'analytics_update',
        operation: 'update',
        data: { eventType, analytics: analyticsData },
        timestamp: new Date().toISOString(),
        retries: 0,
        priority: 'low'
      };
      
      await this.addToSyncQueue(syncOperation);
      
      console.log(`📈 Analytics updated for event: ${eventType}`);
      
    } catch (error) {
      console.error('❌ Error updating analytics:', error);
    }
  }

  async getAnalyticsData() {
    try {
      const data = await AsyncStorage.getItem('analytics_data');
      return data ? JSON.parse(data) : this.getDefaultAnalyticsData();
    } catch (error) {
      return this.getDefaultAnalyticsData();
    }
  }

  getDefaultAnalyticsData() {
    return {
      assessments: {
        total: 0,
        completed: 0,
        average_score: 0,
        completion_rate: 0
      },
      applications: {
        total: 0,
        successful: 0,
        pending: 0,
        success_rate: 0
      },
      interviews: {
        scheduled: 0,
        completed: 0,
        passed: 0,
        pass_rate: 0
      },
      business_metrics: {
        time_to_hire: 0,
        cost_per_hire: 0,
        candidate_satisfaction: 0,
        employer_satisfaction: 0
      }
    };
  }

  calculateAverageScore(analyticsData, newScore) {
    const totalAssessments = analyticsData.assessments.completed;
    const currentAverage = analyticsData.assessments.average_score;
    
    if (totalAssessments === 0) {
      return newScore;
    }
    
    return ((currentAverage * (totalAssessments - 1)) + newScore) / totalAssessments;
  }

  // Public API Methods
  async getAssessmentHistory(candidateId) {
    try {
      const assessments = await AsyncStorage.getItem('table_assessments');
      const assessmentData = assessments ? JSON.parse(assessments) : [];
      
      return assessmentData.filter(assessment => assessment.candidateId === candidateId);
    } catch (error) {
      console.error('Error getting assessment history:', error);
      return [];
    }
  }

  async getUpcomingAssessments(candidateId) {
    try {
      const workflows = await AsyncStorage.getItem('table_workflows');
      const workflowData = workflows ? JSON.parse(workflows) : [];
      
      return workflowData.filter(workflow => 
        workflow.candidateId === candidateId && 
        workflow.status === 'scheduled' &&
        new Date(workflow.scheduledDate) > new Date()
      );
    } catch (error) {
      console.error('Error getting upcoming assessments:', error);
      return [];
    }
  }

  async getSyncStatus() {
    return {
      isOnline: !this.offlineMode,
      pendingOperations: this.syncQueue.length,
      lastSyncAttempt: this.lastSyncAttempt || null,
      isInitialized: this.isInitialized
    };
  }

  // Cleanup methods
  async cleanup() {
    if (this.networkCheckInterval) {
      clearInterval(this.networkCheckInterval);
    }
    
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    console.log('🧹 Comprehensive Integration Service cleaned up');
  }
}

// Export singleton instance
const comprehensiveIntegrationService = new ComprehensiveIntegrationService();
export default comprehensiveIntegrationService;

